import { useState, useEffect, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { CheckCircle, XCircle, Spinner } from "@phosphor-icons/react";

export default function OrderSuccess() {
  const [params] = useSearchParams();
  const { clearCart } = useCart();
  const { user } = useAuth();
  const [status, setStatus] = useState("checking"); // checking, paid, failed, timeout
  const cleared = useRef(false);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) { setStatus("failed"); return; }

    let attempts = 0;
    const poll = async () => {
      if (attempts >= 6) { setStatus("timeout"); return; }
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          if (!cleared.current) { clearCart(); cleared.current = true; }
          return;
        }
        if (data.status === "expired") { setStatus("failed"); return; }
      } catch { /* keep polling */ }
      attempts++;
      setTimeout(poll, 2000);
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="max-w-xl mx-auto px-6 py-32 text-center" data-testid="order-success-page">
      {status === "checking" && (
        <>
          <Spinner size={48} className="mx-auto text-[#3F4E4F] animate-spin" />
          <h1 className="font-serif text-3xl text-stone-900 mt-6">Confirmando tu pago...</h1>
          <p className="text-stone-500 mt-3">Un momento por favor.</p>
        </>
      )}
      {status === "paid" && (
        <>
          <CheckCircle size={56} weight="fill" className="mx-auto text-[#166534]" />
          <h1 className="font-serif text-4xl text-stone-900 mt-6" data-testid="payment-success">¡Gracias por tu compra!</h1>
          <p className="text-stone-500 mt-3">Tu pago se procesó correctamente. Recibirás tu pedido pronto.</p>
          <div className="flex gap-4 justify-center mt-8">
            {user
              ? <Link to="/orders" className="bg-[#3F4E4F] text-white px-8 py-3 hover:bg-[#2C3637]" data-testid="view-orders-link">Ver mis pedidos</Link>
              : <Link to="/shop" className="bg-[#3F4E4F] text-white px-8 py-3 hover:bg-[#2C3637]" data-testid="continue-shopping-link">Seguir comprando</Link>}
            {user && <Link to="/shop" className="border border-stone-300 px-8 py-3 hover:bg-stone-100">Seguir comprando</Link>}
          </div>
          {!user && (
            <div className="mt-10 bg-white border border-stone-200 rounded-lg p-6" data-testid="guest-register-invite">
              <p className="text-stone-700 text-sm">¿Desea guardar sus datos y ver el historial de sus pedidos?</p>
              <Link to="/register" className="inline-block mt-3 text-[#3F4E4F] underline text-sm" data-testid="optional-register-link">Crear una cuenta (opcional)</Link>
            </div>
          )}
        </>
      )}
      {(status === "failed" || status === "timeout") && (
        <>
          <XCircle size={56} weight="fill" className="mx-auto text-[#991B1B]" />
          <h1 className="font-serif text-3xl text-stone-900 mt-6" data-testid="payment-failed">
            {status === "timeout" ? "Verificación pendiente" : "El pago no se completó"}
          </h1>
          <p className="text-stone-500 mt-3">
            {status === "timeout" ? "Tu pago aún se está procesando. Revisa tus pedidos en unos minutos." : "Si el problema persiste, intenta de nuevo."}
          </p>
          <Link to="/cart" className="inline-block mt-8 bg-[#3F4E4F] text-white px-8 py-3 hover:bg-[#2C3637]">Volver al carrito</Link>
        </>
      )}
    </div>
  );
}
